import { Dialog, Stack } from "@mui/material";
import {
  createFileRoute,
  Outlet,
  useMatches,
  useParams,
} from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { z } from "zod";

import { Route as AddCodesRoute } from "./add-codes";
import { Route as EditOfferRoute } from "./edit.$offerSlug";
import { Route as NewOfferRoute } from "./new";
import { Route as NewStaticRoute } from "./new.static";
import { Route as NewUniqueRoute } from "./new.unique";

import { DiscardOfferDialog } from "@/features/CouponCodes/components/DiscardOfferDialog";
import { CouponCodesPage } from "@/features/CouponCodes/CouponCodesPage";
import { RequestCloseProvider } from "@/features/CouponCodes/useRequestClose";

const HEX_BAR_URL = "https://cdn.queen.one/connect/ui/hex-bar.png";

const paperSx = {
  display: "flex",
  flexDirection: "column" as const,
  width: "884px",
  padding: 0,
  boxSizing: "border-box",
  gap: 0,
  borderRadius: "16px",
  background: "#FFF",
  overflow: "hidden",
};

const ROWS_PER_PAGE_OPTIONS = [10, 20, 30, 50, 100] as const;

export type CouponCodesSearch = {
  search?: string;
  page: number;
  pageSize: number;
};

export const SearchSchema = z
  .object({
    search: z.string().optional(),
    page: z
      .union([z.string(), z.number()])
      .transform((v) => (typeof v === "string" ? Number(v) : v))
      .pipe(z.number().int().positive())
      .catch(1),
    pageSize: z
      .union([z.string(), z.number()])
      .transform((v) => (typeof v === "string" ? Number(v) : v))
      .pipe(z.number().int().positive())
      .transform((v) =>
        ROWS_PER_PAGE_OPTIONS.includes(
          v as (typeof ROWS_PER_PAGE_OPTIONS)[number],
        )
          ? v
          : 20,
      )
      .catch(20),
  })
  .transform(
    (s): CouponCodesSearch => ({
      search: s.search,
      page: Math.max(1, s.page),
      pageSize: ROWS_PER_PAGE_OPTIONS.includes(
        s.pageSize as (typeof ROWS_PER_PAGE_OPTIONS)[number],
      )
        ? s.pageSize
        : 20,
    }),
  );

export const Route = createFileRoute("/_auth/settings/$siteId/coupon-codes")({
  validateSearch: (raw): CouponCodesSearch => SearchSchema.parse(raw),
  component: CouponCodesLayout,
});

function CouponCodesLayout() {
  const { siteId } = useParams({ strict: false });
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const matches = useMatches();
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const onSearchParamsChange = useCallback(
    (updates: Partial<CouponCodesSearch>) => {
      void navigate({
        search: (prev) => ({ ...prev, ...updates }),
        replace: true,
      });
    },
    [navigate],
  );

  const isEditMode = matches.some((m) => m.routeId === EditOfferRoute.id);

  const isModalOpen = matches.some(
    (m) =>
      m.routeId === AddCodesRoute.id ||
      m.routeId === EditOfferRoute.id ||
      m.routeId === NewOfferRoute.id ||
      m.routeId === NewStaticRoute.id ||
      m.routeId === NewUniqueRoute.id,
  );

  const handleDiscard = () => {
    setShowDiscardDialog(false);
    void navigate({
      to: "/settings/$siteId/coupon-codes",
      params: { siteId: siteId ?? "" },
      search: true,
    });
  };

  const requestClose = () => setShowDiscardDialog(true);

  return (
    <>
      <CouponCodesPage
        searchParams={search}
        onSearchParamsChange={onSearchParamsChange}
      />
      <Dialog
        open={isModalOpen}
        onClose={(_event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        }}
        maxWidth={false}
        slotProps={{
          paper: { sx: paperSx },
        }}
      >
        <img
          src={HEX_BAR_URL}
          alt=""
          style={{ width: "100%", height: "8px", objectFit: "cover" }}
        />
        <Stack sx={{ flex: 1, padding: 8 }}>
          <RequestCloseProvider value={requestClose}>
            <Outlet />
          </RequestCloseProvider>
        </Stack>
      </Dialog>
      <DiscardOfferDialog
        open={showDiscardDialog}
        isEditMode={isEditMode}
        onCancel={() => setShowDiscardDialog(false)}
        onDiscard={handleDiscard}
      />
    </>
  );
}
